import React from 'react';
import { Button, Icon } from '@tviklax/ui-components';
import {
    PageContainer,
    Header,
    Logo,
    Nav,
    NavLink,
    AuthButtons,
    HeroGrid,
    HeroDivider,
    HeroDiagramColumn,
    HeroDiagramScaler,
    HeroCopyColumn,
    HeroEyebrow,
    HeroTitle,
    HeroSubtitle,
    HeroCtaRow,
    HeroPrimaryButton,
    HeroSecondaryButton,
    HeroMetaRow,
    StatementSection,
    StatementText,
    StatementSub,
    ConvictionSection,
    ConvictionInner,
    ConvictionEyebrow,
    ConvictionTitle,
    ConvictionText,
    StatsGrid,
    StatCell,
    StatValue,
    StatLabel,
    FlowSection,
    FlowInner,
    FlowHeader,
    FlowEyebrow,
    FlowTitle,
    FlowSubtitle,
    FlowStepper,
    FlowStep,
    FlowConnector,
    FlowStepCircle,
    FlowStepLabel,
    FlowStepDesc,
    FeaturesSection,
    FeaturesInner,
    SectionEyebrow,
    SectionTitle,
    SectionSubtitle,
    FeaturesGrid,
    FeatureCard,
    FeatureIcon,
    FeatureTitle,
    FeatureDescription,
    PartnersSection,
    PartnersInner,
    LogoGrid,
    LogoCell,
    LogoEmblem,
    LogoImage,
    LogoName,
    ExportLaneSection,
    ExportLaneInner,
    ExportEyebrow,
    ExportTitle,
    ExportText,
    ExportCtaRow,
    ExportPriceNote,
    PillRow,
    PillTag,
    OrderCard,
    PlatformScreenshot,
    OrderCardFooter,
    OrderCardFooterText,
    LanguageRow,
    PricingSection,
    PricingInner,
    PricingEyebrow,
    PricingHeadline,
    PricingSub,
    PricingNote,
    PricingGrid,
    PricingCard,
    PricingBadge,
    PricingCardName,
    PricingCardPrice,
    PricingCardMeta,
    PricingCardFeatureList,
    PricingCardFeature,
    PricingCardCta,
    PricingCardFootnote,
    FooterSection,
    FooterBottom,
    FooterLink,
} from './styles';
import { TviklaHubDiagram } from './TviklaHubDiagram';

interface LandingPageProps {
    onLogin: () => void;
    onRegister: () => void;
    onNavigateToCreateOrder?: () => void;
    onNavigateToMarketplace?: () => void;
    onNavigateToPayments?: () => void;
    onNavigateToPrivacy?: () => void;
    onNavigateToTerms?: () => void;
    onNavigateToHelp?: () => void;
}

const FLOW_STEPS: Array<{ label: string; desc: string; icon: 'Package' | 'Calendar' | 'Umbrella' | 'ShieldCheck' | 'Ship' | 'CheckCircle' | 'DollarSign' }> = [
    { label: 'Create Order', desc: 'Buyer & exporter terms locked, documents drafted.', icon: 'Package' },
    { label: 'Production & Booking', desc: 'Production status tracked stage by stage, vessel booking confirmed.', icon: 'Calendar' },
    { label: 'Cargo Insured', desc: 'Shipment covered against loss or damage before it leaves the yard.', icon: 'Umbrella' },
    { label: 'Customs Filed', desc: 'Shipping bill filed via ICEGATE, LEO issued.', icon: 'ShieldCheck' },
    { label: 'In Transit', desc: 'Container tracked via ULIP across ports & rail.', icon: 'Ship' },
    { label: 'Delivered', desc: 'Goods received, proof of delivery logged.', icon: 'CheckCircle' },
    { label: 'Settled', desc: 'Payment reconciled, eBRC filed, ledger closed.', icon: 'DollarSign' },
];

const FEATURES = [
    { icon: 'LayoutDashboard' as const, title: 'Dashboard', desc: 'Live view across every order, shipment and rupee in transit.' },
    { icon: 'Calendar' as const, title: 'Production & Scheduling', desc: 'Live production status updates, from Start to Dispatch, alongside vessel booking on one calendar.' },
    { icon: 'ShoppingCart' as const, title: 'Any Order, One System', desc: 'Sourced a supplier on the Tvikla Marketplace, or already trading with one? Either way, the order runs through the same tracking from here.' },
    { icon: 'ShieldCheck' as const, title: 'Customs & Compliance', desc: 'IEC, GST, AD code and ICEGATE filings tracked to the Let Export Order.' },
    { icon: 'Ship' as const, title: 'Logistics Tracking', desc: 'No more waiting on the phone; view container and shipment tracking live on ULIP.' },
    { icon: 'Umbrella' as const, title: 'Insurance', desc: 'Every shipment is tracked and fully protected against loss or damage before it leaves the yard.' },
    { icon: 'CreditCard' as const, title: 'Payments & Finance', desc: 'Multi-currency invoicing, collections and reconciliation against every order.' },
    { icon: 'MessageCircle' as const, title: 'Messaging', desc: 'Buyers and suppliers share one thread, yet experience it entirely in their own native language.' },
];

// Government / regulatory systems: shown as styled initials — there is no
// consumer "logo" for these portals in the western sense.
// Carriers & payment providers: real companies with real marks. Stripe and
// Razorpay use their actual brand glyph (via simple-icons, CC0-licensed).
// Maersk and Hapag Lloyd use their actual logo files, supplied directly.
// NOTE: HDFC Bank is intentionally NOT listed here — that partnership is
// still in talks, not signed. Do not re-add it until it's finalized.
const PARTNERS = [
    { name: 'ICEGATE', imageSrc: '/logos/icegate.png' },
    { name: 'ULIP', imageSrc: '/logos/ulip.png' },
    { name: 'ECGC', imageSrc: '/logos/ecgc.png' },
    { name: 'MAERSK', imageSrc: '/logos/maersk.svg' },
    { name: 'Hapag Lloyd', imageSrc: '/logos/hapag-lloyd.png' },
    { name: 'DGFT', imageSrc: '/logos/dgft.png' },
    { name: 'FOIS', mono: 'FO', color: '#7c2d12' },
    { name: 'GST', mono: 'GS', color: '#15803d' },
    { name: 'FASTag', mono: 'FT', color: '#1d4ed8' },
    {
        name: 'Razorpay',
        color: '#0c2451',
        iconPath: 'M22.436 0l-11.91 7.773-1.174 4.276 6.625-4.297L11.65 24h4.391l6.395-24zM14.26 10.098L3.389 17.166 1.564 24h9.008l3.688-13.902Z',
    },
    {
        name: 'Stripe',
        color: '#635bff',
        iconPath: 'M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.594-7.305h.003z',
    },
];

// Two-sided pricing: buyers stay free no matter how they arrived — via the
// Marketplace or an existing exporter relationship — since they already pay
// through the price of goods. Exporters scale with shipment volume, since
// that's where compliance/logistics/payments load actually grows, regardless
// of where the shipment originated. Enterprise breaks the volume cap entirely.
const PRICING_TIERS: Array<{
    name: string;
    price: string;
    priceSuffix?: string;
    meta: string;
    features: string[];
    cta: string;
    action: 'marketplace' | 'register' | 'enterprise';
    secondaryCta?: string;
    secondaryAction?: 'create-order';
    filled?: boolean;
    featured?: boolean;
}> = [
    {
        name: 'Buyer',
        price: 'Free',
        meta: 'For any buyer placing an order with an exporter — found on the Marketplace, or already your own supplier.',
        features: [
            'Browse verified exporters on the Marketplace',
            'Or create an order directly with an exporter you already work with',
            'Live production, customs & shipment tracking, either way',
        ],
        cta: 'Browse the Marketplace',
        action: 'marketplace',
        secondaryCta: 'Already have an exporter? Create your order free',
        secondaryAction: 'create-order',
        filled: false,
    },
    {
        name: 'Exporter · Starter',
        price: '$7',
        priceSuffix: '/month',
        meta: '3 months free. For exporters just getting started — up to 3 active shipments/month.',
        features: [
            'Dashboard & order management',
            'Production status updates',
            'ICEGATE shipping bill filing',
            'Buyer messaging',
        ],
        cta: 'Start free trial',
        action: 'register',
        filled: true,
    },
    {
        name: 'Exporter · Growth',
        price: '$39',
        priceSuffix: '/month',
        meta: 'For exporters running regular volume — up to 20 active shipments/month.',
        features: [
            'Everything in Starter',
            'Full customs & compliance suite (ICEGATE, GST, RBI/FEMA)',
            'ULIP logistics tracking',
            'Payments reconciliation & eBRC filing',
        ],
        cta: 'Start Growth',
        action: 'register',
        filled: true,
        featured: true,
    },
    {
        name: 'Enterprise',
        price: 'Custom',
        meta: 'For high-volume exporters & trading houses — unlimited shipments.',
        features: [
            'Everything in Growth',
            'Dedicated onboarding & support',
            'ERP / API integrations',
            'Volume-based pricing',
        ],
        cta: 'Talk to sales',
        action: 'enterprise',
        filled: false,
    },
];

export const LandingPage: React.FC<LandingPageProps> = ({
    onLogin,
    onRegister,
    onNavigateToCreateOrder,
    onNavigateToMarketplace,
    onNavigateToPrivacy,
    onNavigateToTerms,
    onNavigateToHelp,
}) => {
    return (
        <PageContainer>
            <Header>
                <Logo>
                    <img src="/tviklax-logo.png" alt="Tvikla Logo" style={{ height: '48px' }} />
                </Logo>
                <Nav>
                    <NavLink href="#platform">Platform</NavLink>
                    <NavLink href="#compliance">Compliance</NavLink>
                    <NavLink href="#how-it-works">How it works</NavLink>
                    <NavLink href="#pricing">Pricing</NavLink>
                </Nav>
                <AuthButtons>
                    <Button variant="outline" onClick={onLogin}>Log In</Button>
                    <Button onClick={onRegister}>Start Free</Button>
                </AuthButtons>
            </Header>

            {/* 0. HERO — pixel recreation of the "Tvikla Hero" hub-spoke design handoff */}
            <HeroGrid>
                <HeroDiagramColumn>
                    <HeroDiagramScaler>
                        <TviklaHubDiagram />
                    </HeroDiagramScaler>
                </HeroDiagramColumn>

                <HeroDivider />

                <HeroCopyColumn>
                    <HeroEyebrow>
                        <Icon name="Globe" size={19} color="#F2691F" />
                        Tvikla OS &middot; Export Platform
                    </HeroEyebrow>
                    <HeroTitle>
                        The Complete Infrastructure for<br />
                        <span>Modern Trade</span>
                    </HeroTitle>
                    <HeroSubtitle>
                        Orchestrate production updates, book and track cross-border logistics, and manage global payments,
                        all in one place &mdash; Tvikla OS. Unify your operations with customs clearance, compliance
                        checks, multilingual messaging, and automated document generation on a single software framework.
                    </HeroSubtitle>
                    <HeroCtaRow>
                        <HeroPrimaryButton onClick={onRegister}>Get Started</HeroPrimaryButton>
                        <HeroSecondaryButton onClick={onLogin}>Log In</HeroSecondaryButton>
                    </HeroCtaRow>
                    <HeroMetaRow>
                        <span>11 modules</span><span>&middot;</span><span>One framework</span><span>&middot;</span><span>Zero handoffs</span>
                    </HeroMetaRow>
                </HeroCopyColumn>
            </HeroGrid>

            {/* STATEMENT — you don't need a dozen tools, you need one */}
            <StatementSection>
                <StatementText>
                    You don&apos;t need a dozen tools to export or import.<br />
                    You need <span>only one &mdash; Tvikla OS.</span>
                </StatementText>
                <StatementSub>
                    From creating the order to closing the settlement — production updates, compliance, logistics,
                    insurance, payments, documents and messaging all run on a single platform, start to end, whether
                    the order started on the Marketplace or came in on its own.
                </StatementSub>
            </StatementSection>

            {/* 1. HOW IT WORKS — the order lifecycle as one roadmap */}
            <FlowSection id="how-it-works">
                <FlowInner>
                    <FlowHeader>
                        <FlowEyebrow>How it works</FlowEyebrow>
                        <FlowTitle>Orders tracked from start to end</FlowTitle>
                        <FlowSubtitle>
                            The same roadmap runs behind every order — sourced on the Marketplace or brought in
                            directly — from the moment it&apos;s created to the moment the payment settles.
                        </FlowSubtitle>
                    </FlowHeader>
                    <FlowStepper>
                        {FLOW_STEPS.map((step, i) => (
                            <FlowStep key={step.label}>
                                <FlowConnector />
                                <FlowStepCircle $final={i === FLOW_STEPS.length - 1}>
                                    <Icon name={step.icon} size={20} />
                                </FlowStepCircle>
                                <FlowStepLabel>{i + 1}. {step.label}</FlowStepLabel>
                                <FlowStepDesc>{step.desc}</FlowStepDesc>
                            </FlowStep>
                        ))}
                    </FlowStepper>
                </FlowInner>
            </FlowSection>

            {/* 3. PLATFORM — functionality expansion */}
            <FeaturesSection id="platform">
                <FeaturesInner>
                    <SectionEyebrow>The platform</SectionEyebrow>
                    <SectionTitle>One system for every part of the export</SectionTitle>
                    <SectionSubtitle>
                        Tvikla OS is built on real, live connections to your carriers, banks and customs systems.
                        Every module works together and stays compliant automatically, so nothing falls through
                        the cracks between teams.
                    </SectionSubtitle>
                    <FeaturesGrid>
                        {FEATURES.map((f) => (
                            <FeatureCard key={f.title}>
                                <FeatureIcon>
                                    <Icon name={f.icon} size={22} />
                                </FeatureIcon>
                                <FeatureTitle>{f.title}</FeatureTitle>
                                <FeatureDescription>{f.desc}</FeatureDescription>
                            </FeatureCard>
                        ))}
                    </FeaturesGrid>
                </FeaturesInner>
            </FeaturesSection>

            {/* 4. PARTNERS — the real systems Tvikla is wired into */}
            <PartnersSection>
                <PartnersInner>
                    <SectionEyebrow>Integrates with</SectionEyebrow>
                    <SectionTitle>Wired into the systems that actually clear, move, and pay for your export</SectionTitle>
                    <SectionSubtitle style={{ marginBottom: '2.5rem' }}>
                        Not a dashboard on top of guesswork — direct lines into customs, logistics and banking
                        infrastructure.
                    </SectionSubtitle>
                    <LogoGrid>
                        {PARTNERS.map((p) => (
                            <LogoCell key={p.name}>
                                {p.imageSrc ? (
                                    <LogoImage src={p.imageSrc} alt={`${p.name} logo`} />
                                ) : (
                                    <LogoEmblem $color={p.color!}>
                                        {p.iconPath ? (
                                            <svg viewBox="0 0 24 24" width={18} height={18} fill="#ffffff">
                                                <path d={p.iconPath} />
                                            </svg>
                                        ) : (
                                            p.mono
                                        )}
                                    </LogoEmblem>
                                )}
                                <LogoName>{p.name}</LogoName>
                            </LogoCell>
                        ))}
                    </LogoGrid>
                </PartnersInner>
            </PartnersSection>

            {/* 5. EXPORT LANE — the buyer-facing close */}
            <ExportLaneSection>
                <ExportLaneInner>
                    <div>
                        <ExportEyebrow>
                            <Icon name="Globe" size={13} />
                            Tvikla OS &middot; India &#8646; USA Trade Lane
                        </ExportEyebrow>
                        <ExportTitle>
                            Trade with India or the US<br />like it&apos;s next door.
                        </ExportTitle>
                        <ExportText>
                            Tvikla OS runs the whole trade lifecycle behind the deal — production tracked stage
                            by stage, vessel and container booked, cargo insured, and every document generated
                            automatically. <b>Shipping bills filed through ICEGATE</b>, containers <b>tracked
                            through ULIP</b>, payments settled and reconciled to the rupee, buyer and supplier
                            messaging on one thread. Whichever side is exporting, both partners work off the
                            same live screen, each in their own language.
                        </ExportText>
                        <ExportCtaRow>
                            <Button onClick={onRegister}>Start free for 3 months</Button>
                            <Button variant="outline" onClick={onNavigateToCreateOrder}>Walk through a live order</Button>
                        </ExportCtaRow>
                        <ExportPriceNote>Buyers trade for free &middot; Exporters from $7/month &middot; no card for the trial</ExportPriceNote>
                        <PillRow>
                            <PillTag>ICEGATE e-filing</PillTag>
                            <PillTag>ULIP tracking</PillTag>
                            <PillTag>RBI / FEMA compliance</PillTag>
                            <PillTag>GST &middot; IEC &middot; PAN verified</PillTag>
                        </PillRow>
                    </div>

                    <OrderCard>
                        <PlatformScreenshot
                            src="/screenshots/dashboard-preview.png"
                            alt="Tvikla OS dashboard — live shipment, customs and payment status"
                        />
                        <OrderCardFooter>
                            <OrderCardFooterText>Same dashboard, same numbers, both sides of the deal.</OrderCardFooterText>
                            <LanguageRow>EN &middot; &#2361;&#2367;&#2306;&#2342;&#2368; &middot; &#2980;&#2990;&#3007;&#2996;&#3021;</LanguageRow>
                        </OrderCardFooter>
                    </OrderCard>
                </ExportLaneInner>
            </ExportLaneSection>

            {/* 6. CONVICTION — why filings, not promises, move cargo (final reassurance before pricing) */}
            <ConvictionSection id="compliance">
                <ConvictionInner>
                    <div>
                        <ConvictionEyebrow>Why US and Indian partners can relax</ConvictionEyebrow>
                        <ConvictionTitle>
                            If it isn&apos;t filed,<br />it doesn&apos;t ship.
                        </ConvictionTitle>
                        <ConvictionText>
                            Cargo cannot enter the yard until the shipping bill is filed electronically through
                            ICEGATE and the Let Export Order is issued. Tvikla OS drafts that bill the moment the
                            order lands, completes it from the carrier booking, files it, and holds the shipment
                            until the LEO comes back. No cargo moves on a promise.
                        </ConvictionText>
                        <ConvictionText>
                            From there, movement is reported by ULIP — India&apos;s national logistics backbone,
                            wired into railways, ports, customs and highways. Your tracking isn&apos;t the supplier
                            telling you it left. It&apos;s the government systems saying so.
                        </ConvictionText>
                    </div>
                    <StatsGrid>
                        <StatCell>
                            <StatValue>30+</StatValue>
                            <StatLabel>Govt systems via ULIP</StatLabel>
                        </StatCell>
                        <StatCell>
                            <StatValue>100+</StatValue>
                            <StatLabel>Live government APIs</StatLabel>
                        </StatCell>
                        <StatCell>
                            <StatValue $accent>100%</StatValue>
                            <StatLabel>Sellers KYC-verified</StatLabel>
                        </StatCell>
                        <StatCell>
                            <StatValue>11</StatValue>
                            <StatLabel>Modules, one ledger</StatLabel>
                        </StatCell>
                    </StatsGrid>
                </ConvictionInner>
            </ConvictionSection>

            {/* 7. PRICING */}
            <PricingSection id="pricing">
                <PricingInner>
                    <PricingEyebrow>Pricing</PricingEyebrow>
                    <PricingHeadline>Priced for how you actually trade</PricingHeadline>
                    <PricingSub>
                        If you&apos;re buying — whether you found your exporter on the Marketplace or already work
                        with one — you trade for free, you&apos;re the demand exporters need. If you&apos;re
                        exporting, you pick a plan that matches your shipment volume, not a flat bundle that costs
                        the same whether you ship twice a year or twice a week.
                    </PricingSub>
                    <PricingGrid>
                        {PRICING_TIERS.map((tier) => (
                            <PricingCard key={tier.name} $featured={tier.featured}>
                                {tier.featured && <PricingBadge>Most popular</PricingBadge>}
                                <PricingCardName>{tier.name}</PricingCardName>
                                <PricingCardPrice>
                                    {tier.price}
                                    {tier.priceSuffix && <span>{tier.priceSuffix}</span>}
                                </PricingCardPrice>
                                <PricingCardMeta>{tier.meta}</PricingCardMeta>
                                <PricingCardFeatureList>
                                    {tier.features.map((feature) => (
                                        <PricingCardFeature key={feature}>
                                            <Icon name="CheckCircle" size={15} />
                                            {feature}
                                        </PricingCardFeature>
                                    ))}
                                </PricingCardFeatureList>
                                <PricingCardCta
                                    $filled={tier.filled}
                                    onClick={
                                        tier.action === 'marketplace' ? onNavigateToMarketplace :
                                        tier.action === 'register' ? onRegister :
                                        undefined
                                    }
                                >
                                    {tier.cta}
                                </PricingCardCta>
                                {tier.secondaryCta ? (
                                    <PricingCardFootnote
                                        as="button"
                                        type="button"
                                        onClick={tier.secondaryAction === 'create-order' ? onNavigateToCreateOrder : undefined}
                                        style={{
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                            background: 'none',
                                            border: 'none',
                                            font: 'inherit',
                                            width: '100%',
                                        }}
                                    >
                                        {tier.secondaryCta}
                                    </PricingCardFootnote>
                                ) : (
                                    // Reserves the same footer row on every card so the primary CTA button
                                    // lines up across tiers regardless of whether a tier has a secondary link.
                                    <PricingCardFootnote aria-hidden style={{ visibility: 'hidden' }}>
                                        placeholder
                                    </PricingCardFootnote>
                                )}
                            </PricingCard>
                        ))}
                    </PricingGrid>
                    <PricingNote>Cancel anytime. No setup fees. No card required for the trial.</PricingNote>
                </PricingInner>
            </PricingSection>

            <FooterSection>
                <FooterBottom>
                    <div>&copy; {new Date().getFullYear()} Tvikla. All rights reserved.</div>
                    <div style={{ display: 'flex', gap: '2rem' }}>
                        <FooterLink onClick={onNavigateToPrivacy}>Privacy Policy</FooterLink>
                        <FooterLink onClick={onNavigateToTerms}>Terms of Service</FooterLink>
                        <FooterLink onClick={onNavigateToHelp}>Help Center</FooterLink>
                    </div>
                </FooterBottom>
            </FooterSection>
        </PageContainer>
    );
};
